// src/context/UserContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  DashboardData,
  DigitalProductPurchase,
  PhysicalProductPurchase,
  UserTransaction,
} from "../api/interface/purchases.interface";
import { Order } from "../api/interface/orders.interface";
import { showToast } from "../components/ui/notifications/showToast";
import { userPurchasesService } from "../api/service/purchases.service";
import { orderService } from "../api/service/order.service";

// ===============================
// TYPES
// ===============================
interface UserContextType {
  // Dashboard
  dashboardData: DashboardData | null;
  isLoadingDashboard: boolean;
  refreshDashboard: () => Promise<void>;

  // Produits digitaux
  digitalProducts: DigitalProductPurchase[] | null;
  isLoadingDigitalProducts: boolean;
  refreshDigitalProducts: () => Promise<void>;

  // Produits physiques
  physicalProducts: PhysicalProductPurchase[] | null;
  isLoadingPhysicalProducts: boolean;
  refreshPhysicalProducts: () => Promise<void>;

  // Transactions
  transactions: UserTransaction[] | null;
  isLoadingTransactions: boolean;
  refreshTransactions: () => Promise<void>;

  // Commandes (orders)
  orders: Order[] | null;
  isLoadingOrders: boolean;
  refreshOrders: () => Promise<void>;

  // Dernière commande
  latestOrder: Order | null;
  isLoadingLatestOrder: boolean;
  refreshLatestOrder: () => Promise<void>;
}

// ===============================
// CONTEXT
// ===============================
const UserContext = createContext<UserContextType | undefined>(undefined);

// ===============================
// PROVIDER
// ===============================
export const UserProvider = ({ children }: { children: ReactNode }) => {
  // ===============================
  // STATE
  // ===============================
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [digitalProducts, setDigitalProducts] = useState<DigitalProductPurchase[] | null>(null);
  const [physicalProducts, setPhysicalProducts] = useState<PhysicalProductPurchase[] | null>(null);
  const [transactions, setTransactions] = useState<UserTransaction[] | null>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [latestOrder, setLatestOrder] = useState<Order | null>(null);

  // ===============================
  // LOADING STATES
  // ===============================
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [isLoadingDigitalProducts, setIsLoadingDigitalProducts] = useState(false);
  const [isLoadingPhysicalProducts, setIsLoadingPhysicalProducts] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isLoadingLatestOrder, setIsLoadingLatestOrder] = useState(false);

  // ===============================
  // REFS (ANTI DOUBLE CALL)
  // ===============================
  const fetchingDashboardRef = useRef(false);
  const fetchingDigitalProductsRef = useRef(false);
  const fetchingPhysicalProductsRef = useRef(false);
  const fetchingTransactionsRef = useRef(false);
  const fetchingOrdersRef = useRef(false);
  const fetchingLatestOrderRef = useRef(false);

  // ===============================
  // LOADERS
  // ===============================
  const loadDashboard = useCallback(async () => {
    if (fetchingDashboardRef.current) return;
    fetchingDashboardRef.current = true;
    setIsLoadingDashboard(true);
    try {
      const response = await userPurchasesService.getDashboard();
      if (response.success) {
        setDashboardData(response.data);
      } else {
        showToast({ title: "Erreur", description: response.message, type: "error" });
      }
    } catch (error: any) {
      showToast({ title: "Erreur", description: "Impossible de charger le tableau de bord", type: "error" });
      console.error("Dashboard loading error:", error);
    } finally {
      fetchingDashboardRef.current = false;
      setIsLoadingDashboard(false);
    }
  }, []);

  const loadDigitalProducts = useCallback(async () => {
    if (fetchingDigitalProductsRef.current) return;
    fetchingDigitalProductsRef.current = true;
    setIsLoadingDigitalProducts(true);
    try {
      const response = await userPurchasesService.getDigitalProducts();
      if (response.success) {
        setDigitalProducts(response.data);
      } else {
        showToast({ title: "Erreur", description: response.message, type: "error" });
      }
    } catch (error) {
      showToast({ title: "Erreur", description: "Impossible de charger les produits digitaux", type: "error" });
      console.error("Digital products loading error:", error);
    } finally {
      fetchingDigitalProductsRef.current = false;
      setIsLoadingDigitalProducts(false);
    }
  }, []);

  const loadPhysicalProducts = useCallback(async () => {
    if (fetchingPhysicalProductsRef.current) return;
    fetchingPhysicalProductsRef.current = true;
    setIsLoadingPhysicalProducts(true);
    try {
      const response = await userPurchasesService.getPhysicalProducts();
      if (response.success) {
        setPhysicalProducts(response.data);
      } else {
        showToast({ title: "Erreur", description: response.message, type: "error" });
      }
    } catch (error) {
      showToast({ title: "Erreur", description: "Impossible de charger les produits physiques", type: "error" });
      console.error("Physical products loading error:", error);
    } finally {
      fetchingPhysicalProductsRef.current = false;
      setIsLoadingPhysicalProducts(false);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    if (fetchingTransactionsRef.current) return;
    fetchingTransactionsRef.current = true;
    setIsLoadingTransactions(true);
    try {
      const response = await userPurchasesService.getTransactions();
      if (response.success) {
        setTransactions(response.data);
      } else {
        showToast({ title: "Erreur", description: response.message, type: "error" });
      }
    } catch (error) {
      showToast({ title: "Erreur", description: "Impossible de charger les transactions", type: "error" });
      console.error("Transactions loading error:", error);
    } finally {
      fetchingTransactionsRef.current = false;
      setIsLoadingTransactions(false);
    }
  }, []);

  // src/context/UserContext.tsx (dans le provider)

  const loadOrders = useCallback(async () => {
    if (fetchingOrdersRef.current) return;
    fetchingOrdersRef.current = true;
    setIsLoadingOrders(true);
    try {
      const response = await orderService.getMyOrders();
      if (response.success) {
        // ✅ Les commandes sont dans response.data.data
        setOrders(response.data.data);
      } else {
        showToast({ title: "Erreur", description: "Impossible de charger les commandes", type: "error" });
      }
    } catch (error) {
      showToast({ title: "Erreur", description: "Impossible de charger les commandes", type: "error" });
      console.error("Orders loading error:", error);
    } finally {
      fetchingOrdersRef.current = false;
      setIsLoadingOrders(false);
    }
  }, []);

  const loadLatestOrder = useCallback(async () => {
    if (fetchingLatestOrderRef.current) return;
    fetchingLatestOrderRef.current = true;
    setIsLoadingLatestOrder(true);
    try {
      const response = await orderService.getLatestOrder();
      if (response.success) {
        setLatestOrder(response.data);
      } else {
        // Pas de toast d'erreur car c'est normal de ne pas avoir de commande
        setLatestOrder(null);
      }
    } catch (error) {
      setLatestOrder(null);
      console.error("Latest order loading error:", error);
    } finally {
      fetchingLatestOrderRef.current = false;
      setIsLoadingLatestOrder(false);
    }
  }, []);

  // ===============================
  // REFRESH
  // ===============================
  const refreshDashboard = useCallback(async () => loadDashboard(), [loadDashboard]);
  const refreshDigitalProducts = useCallback(async () => loadDigitalProducts(), [loadDigitalProducts]);
  const refreshPhysicalProducts = useCallback(async () => loadPhysicalProducts(), [loadPhysicalProducts]);
  const refreshTransactions = useCallback(async () => loadTransactions(), [loadTransactions]);
  const refreshOrders = useCallback(async () => loadOrders(), [loadOrders]);
  const refreshLatestOrder = useCallback(async () => loadLatestOrder(), [loadLatestOrder]);

  // ===============================
  // INIT
  // ===============================
  useEffect(() => {
    loadDashboard();
    loadDigitalProducts();
    loadPhysicalProducts();
    loadTransactions();
    loadOrders();
    loadLatestOrder();
  }, [
    loadDashboard,
    loadDigitalProducts,
    loadPhysicalProducts,
    loadTransactions,
    loadOrders,
    loadLatestOrder,
  ]);

  // ===============================
  // VALUE
  // ===============================
  const value = useMemo(
    () => ({
      dashboardData,
      isLoadingDashboard,
      refreshDashboard,
      digitalProducts,
      isLoadingDigitalProducts,
      refreshDigitalProducts,
      physicalProducts,
      isLoadingPhysicalProducts,
      refreshPhysicalProducts,
      transactions,
      isLoadingTransactions,
      refreshTransactions,
      orders,
      isLoadingOrders,
      refreshOrders,
      latestOrder,
      isLoadingLatestOrder,
      refreshLatestOrder,
    }),
    [
      dashboardData,
      isLoadingDashboard,
      refreshDashboard,
      digitalProducts,
      isLoadingDigitalProducts,
      refreshDigitalProducts,
      physicalProducts,
      isLoadingPhysicalProducts,
      refreshPhysicalProducts,
      transactions,
      isLoadingTransactions,
      refreshTransactions,
      orders,
      isLoadingOrders,
      refreshOrders,
      latestOrder,
      isLoadingLatestOrder,
      refreshLatestOrder,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// ===============================
// HOOK
// ===============================
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser doit être utilisé dans UserProvider");
  }
  return context;
};